import { useEffect, useRef } from "react";

const PrivacyPolicyComp = () => {
  const tocRef = useRef(null);
  const tocPathRef = useRef(null);
  const TOP_MARGIN = 0;
  const BOTTOM_MARGIN = 0.85;

  let pathLength = 0;
  let lastPathStart = 0;
  let lastPathEnd = 0;
  let tocItems = [];

  useEffect(() => {
    const drawPath = () => {
      const toc = tocRef.current;
      const tocPath = tocPathRef.current;
      if (!toc || !tocPath) return;

      tocItems = Array.from(toc.querySelectorAll("li"))
        .map((item) => {
          const anchor = item.querySelector("a");
          const targetId = anchor.getAttribute("href").slice(1);
          const target = document.getElementById(targetId);

          return { listItem: item, anchor, target };
        })
        .filter((item) => !!item.target);

      const path = [];
      let pathIndent;

      tocItems.forEach((item, i) => {
        const x = item.anchor.offsetLeft - 12;
        const y = item.anchor.offsetTop + 4;
        const height = item.anchor.offsetHeight - 12;

        if (i === 0) {
          path.push("M", x, y, "L", x, y + height);
          item.pathStart = 0;
        } else {
          if (pathIndent !== x) path.push("L", pathIndent, y);
          path.push("L", x, y);
          tocPath.setAttribute("d", path.join(" "));
          item.pathStart = tocPath.getTotalLength() || 0;
          path.push("L", x, y + height);
        }

        pathIndent = x;
        tocPath.setAttribute("d", path.join(" "));
        item.pathEnd = tocPath.getTotalLength();
      });

      pathLength = tocPath.getTotalLength();
      sync();
    };

    const sync = () => {
      const contents = document.getElementById("article");
      const windowHeight = contents
        ? contents.offsetHeight
        : window.innerHeight;
      let pathStart = pathLength;
      let pathEnd = 0;
      let visibleItems = 0;

      tocItems.forEach((item) => {
        const rect = item.target.getBoundingClientRect();

        if (
          rect.bottom > windowHeight * TOP_MARGIN &&
          rect.top < windowHeight * (1 - BOTTOM_MARGIN)
        ) {
          pathStart = Math.min(item.pathStart, pathStart);
          pathEnd = Math.max(item.pathEnd, pathEnd);
          visibleItems += 1;
          item.listItem.classList.add("visible");
        } else {
          item.listItem.classList.remove("visible");
        }
      });

      const tocPath = tocPathRef.current;
      if (!tocPath) return;

      if (visibleItems > 0 && pathStart < pathEnd) {
        if (pathStart !== lastPathStart || pathEnd !== lastPathEnd) {
          tocPath.setAttribute("stroke-dashoffset", "1");
          tocPath.setAttribute(
            "stroke-dasharray",
            `1, ${pathStart}, ${pathEnd - pathStart}, ${pathLength}`
          );
          tocPath.setAttribute("opacity", 1);
        }
      } else {
        tocPath.setAttribute("opacity", 0);
      }

      lastPathStart = pathStart;
      lastPathEnd = pathEnd;
    };

    drawPath();
    window.addEventListener("scroll", sync);
    window.addEventListener("resize", drawPath);

    return () => {
      window.removeEventListener("scroll", sync);
      window.removeEventListener("resize", drawPath);
    };
  }, []);

  return (
    <div className="policy-wrapper wrapper-landing">
      <div className="hero">
        <div className="head-landing text-center w-100-landing">
          <div className="subheading-landing">
            LEARN HOW WE RESPECT YOUR PRIVACY
          </div>
          <h1 className="title-landing mb-32-landing">Privacy Policy</h1>
          <div className="description-landing fw-semibold">
            <p className="content">
              In this policy, the words "we", "our", and "us" refer to ZyNerd
              and the privacy mechanism outlined identify personally
              identifiable or personal information that may be collected, how
              such information is used, and the choices You have regarding our
              use of this information. In addition to the terms stated in Terms
              of Service provided under this Website/mobile application
              (together “Platform”), we are committed to protecting Your
              privacy.
            </p>
            <p className="content">
              In addition to the terms stated in Terms of Service provided under
              this Website/mobile application (together “Platform”), we are
              committed to protecting Your privacy.
            </p>
          </div>
        </div>
      </div>

      <div className="row-landing">
        <div className="toc" ref={tocRef}>
          <ul>
            <li>
              <a href="#general" className="content">
                General
              </a>
            </li>
            <li>
              <a href="#additional-terms" className="content">
                Additional Terms
              </a>
            </li>
            <li>
              <a href="#data-collection" className="content">
                Data Collection
              </a>
            </li>
            <li>
              <a href="#otp-authentication" className="content">
                OTP-Based Authentication
              </a>
            </li>
            <li>
              <a href="#data-usage" className="content">
                Data Usage
              </a>
            </li>
            <li>
              <a href="#payments-subscriptions" className="content">
                Payments and Subscriptions
              </a>
            </li>
            <li>
              <a href="#refer-and-earn" className="content">
                Refer and Earn
              </a>
            </li>
            <li>
              <a href="#data-sharing" className="content">
                Data Sharing
              </a>
            </li>
            <li>
              <a href="#data-protection" className="content">
                Data Protection
              </a>
            </li>
            <li>
              <a href="#user-rights" className="content">
                User Rights
              </a>
            </li>
            <li>
              <a href="#data-retention" className="content">
                Data Retention
              </a>
            </li>
            <li>
              <a href="#user-choices" className="content">
                User Choices and Opt-Out
              </a>
            </li>
            <li>
              <a href="#revision-grievance" className="content">
                Revisions and Grievance Reporting
              </a>
            </li>
            <li>
              <a href="#contact" className="content">
                Contact Us
              </a>
            </li>
          </ul>

          <svg
            className="toc-marker"
            width="200"
            height="200"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              ref={tocPathRef}
              stroke="#F85D05"
              strokeWidth="3"
              fill="transparent"
              strokeDasharray="0, 0, 0, 1000"
              strokeLinecap="round"
              strokeLinejoin="round"
              transform="translate(-0.5, -0.5)"
            />
          </svg>
        </div>

        <article id="article" className="article">
          <section id="general">
            <h3 className="secondary">GENERAL</h3>
            <p className="content">
              Only authorized representatives of ZyNerd use any information
              received from you on a need-to-know basis and as consented by you.
              We constantly review our systems and data to secure your personal
              information. In the event of any unauthorized use or disclosure of
              information or upon your complaint, we will investigate and take
              appropriate action.
            </p>
            <p className="content">
              Any user who disagrees with this Privacy Policy and the Terms of
              Service should leave the platform immediately. By continuing to
              use the platform, you accept this policy and any other online
              terms and conditions available on the platform.
            </p>
          </section>

          <section id="additional-terms">
            <h3 className="secondary">APPLICABILITY</h3>
            <p className="content">
              This privacy policy (“Policy”) applies to all users who access the
              platform and are therefore required to read and understand the
              policy before submitting any personal information. By submitting
              personal information, you consent to the use and processing of
              such information in accordance with this policy.
            </p>
          </section>

          <section id="data-collection">
            <h3 className="secondary">DATA COLLECTION</h3>
            <h4>Personal Information:</h4>
            <p className="content">
              We collect personal information, such as your name, identification
              number, email address, age, gender, phone number, and any other
              information provided during registration or any time thereafter.
            </p>
            <h4>Automatic Information:</h4>
            <p className="content">
              We collect information automatically sent over the internet, such
              as IP addresses, browser types, operating systems, URLs, and other
              similar data.
            </p>
          </section>

          <section id="otp-authentication">
            <h3 className="secondary">OTP-BASED AUTHENTICATION</h3>
            <p className="content">
              To access our platform, a user is required to authenticate using
              One-Time Passwords (OTPs) sent via SMS or WhatsApp. We do not use
              traditional passwords for authentication.
            </p>
          </section>

          <section id="data-usage">
            <h3 className="secondary">DATA USAGE</h3>
            <p className="content">We use the collected data for:</p>
            <ul className="list">
              <li>Providing and maintaining our services</li>
              <li>Improving and personalizing our platform</li>
              <li>
                Communicating with you for transactional, service, or
                promotional purposes
              </li>
              <li>Ensuring the security of our platform</li>
            </ul>
          </section>

          <section id="payments-subscriptions">
            <h3 className="secondary">PAYMENTS AND SUBSCRIPTIONS</h3>
            <p className="content">
              We use third-party payment processors to handle payments for user
              subscriptions on packages we offer. These third-party processors
              adhere to stringent security standards to protect your payment
              information. We do not store any payment card details on our
              servers.
            </p>
          </section>

          <section id="refer-and-earn">
            <h3 className="secondary">REFER AND EARN</h3>
            <p className="content">
              We offer a refer-and-earn option that allows users to earn rewards
              by referring others to our platform. Participation in this program
              is voluntary. We may collect and use information related to
              referrals and rewards to manage and track the program.
            </p>
          </section>

          <section id="data-sharing">
            <h3 className="secondary">DATA SHARING</h3>
            <p className="content">
              We do not share, rent, or sell your personal information with
              third parties, except as disclosed in this policy. We may share
              your information with:
            </p>
            <ul className="list">
              <li>Third-party service providers for processing transactions</li>
              <li>Authorities to comply with legal obligations</li>
              <li>Affiliates for research and statistical analysis</li>
            </ul>
          </section>

          <section id="data-protection">
            <h3 className="secondary">DATA PROTECTION</h3>
            <p className="content">
              We use industry-standard security measures, including SSL
              encryption, to protect your data. Users are responsible for
              securing their OTPs and access to their devices. While we strive
              to protect your data, internet transmissions are inherently
              insecure.
            </p>
          </section>

          <section id="user-rights">
            <h3 className="secondary">USER RIGHTS</h3>
            <p className="content">You have the right to:</p>
            <ul className="list">
              <li>Access and modify your personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p className="content">
              To exercise these rights, contact us at
              <a href="mailto:hello@zynerd.com">hello@zynerd.com</a>.
            </p>
          </section>

          <section id="data-retention">
            <h3 className="secondary">DATA RETENTION</h3>
            <p className="content">
              We retain personal information as long as necessary to provide our
              services or as required by law. Upon your request, we will delete
              your data, subject to applicable legal obligations.
            </p>
          </section>

          <section id="user-choices">
            <h3 className="secondary">USER CHOICES AND OPT-OUT</h3>
            <p className="content">
              You can change your contact preferences at any time by logging
              into your account or contacting us at
              <a href="mailto:hello@zynerd.com">hello@zynerd.com</a>. To opt-out
              of communications, follow the instructions in the communication or
              contact us.
            </p>
          </section>

          <section id="revision-grievance">
            <h3 className="secondary">REVISIONS AND GRIEVANCE REPORTING</h3>
            <p className="content">
              We may update this policy at any time. Significant changes will be
              communicated via email or a notice on our platform. If you
              disagree with the changes, you can withdraw your information by
              contacting us.
            </p>
          </section>

          <section id="contact">
            <h3 className="secondary">CONTACT US</h3>
            <p className="content">
              If you have any questions or concerns about this Privacy Policy,
              please contact us at
              <a href="mailto:hello@zynerd.com">hello@zynerd.com</a>.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicyComp;
